const EVENTS = Object.freeze({
    show: 'show.bs.modal',
    shown: 'shown.bs.modal',
    hide: 'hide.bs.modal',
    hidden: 'hidden.bs.modal'
});

function captureScrollStyles() {
    const properties = [
        [document.body, 'overflow'], [document.body, 'padding-right'],
        ...[...document.querySelectorAll('.fixed-top, .fixed-bottom, .is-fixed, .sticky-top')]
            .map(element => [element, 'padding-right']),
        ...[...document.querySelectorAll('.sticky-top')].map(element => [element, 'margin-right'])
    ];
    return properties.map(([element, property]) => ({
        element, property,
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property),
        attribute: element.getAttribute(`data-bs-${property}`)
    }));
}

/** Use the host's Bootstrap bundle, keeping modal transitions and disposal in order. */
export function createBootstrapModal(element, { onHidden = () => {}, Modal = window.bootstrap?.Modal } = {}) {
    if (!Modal) throw new Error('The local Bootstrap bundle must load before the blog component.');

    const modal = new Modal(element, { backdrop: true, keyboard: true, focus: true });
    let phase = 'hidden';
    let opener;
    let operations = Promise.resolve();
    let disposal;
    let startEvent;
    let scrollStyles = [];
    let bodyWasOpen = false;
    const idleWaiters = new Set();

    function setPhase(nextPhase) {
        phase = nextPhase;
        if (phase === 'hidden' || phase === 'shown') {
            for (const resolve of idleWaiters) resolve();
            idleWaiters.clear();
        }
    }

    function started(event, nextPhase, previousPhase, focusedElement) {
        startEvent = event;
        setPhase(nextPhase);
        // Escape/backdrop dismissal happens outside our queue. A host can cancel it
        // after our listener runs, so inspect the final event state after dispatch.
        queueMicrotask(() => {
            if (event.defaultPrevented) {
                if (phase === nextPhase) setPhase(previousPhase);
                if (phase === previousPhase && focusedElement?.isConnected) {
                    focusedElement.focus({ preventScroll: true });
                }
            }
        });
    }

    const listeners = {
        [EVENTS.show]: event => started(event, 'showing', 'hidden'),
        [EVENTS.shown]: () => setPhase('shown'),
        [EVENTS.hide]: event => {
            const focusedElement = document.activeElement;
            started(event, 'hiding', 'shown', focusedElement);
            // Release focus before Bootstrap applies aria-hidden to the dialog.
            if (element.contains(document.activeElement)) document.activeElement.blur();
        },
        [EVENTS.hidden]: () => {
            setPhase('hidden');
            if (opener?.isConnected) opener.focus({ preventScroll: true });
            onHidden();
        }
    };
    for (const [name, listener] of Object.entries(listeners)) element.addEventListener(name, listener);

    async function settle() {
        if (phase === 'showing' || phase === 'hiding') {
            await new Promise(resolve => idleWaiters.add(resolve));
        }
    }

    async function transition(action, previousPhase) {
        startEvent = null;
        modal[action]();
        if (startEvent?.defaultPrevented) setPhase(previousPhase);
        await settle();
    }

    function enqueue(operation) {
        operations = operations.then(operation, operation);
        return operations;
    }

    async function hide() {
        await settle();
        if (phase === 'hidden') return;
        await transition('hide', 'shown');
    }

    return {
        show() {
            if (disposal) return disposal;
            return enqueue(async () => {
                await settle();
                if (phase === 'shown') return;
                opener = document.activeElement;
                scrollStyles = captureScrollStyles();
                bodyWasOpen = document.body.classList.contains('modal-open');
                await transition('show', 'hidden');
            });
        },
        hide() {
            if (disposal) return disposal;
            return enqueue(hide);
        },
        dispose() {
            // Vue cannot await unmount hooks. Finish an in-flight transition before disposing
            // so Bootstrap can remove its backdrop and release body scrolling itself.
            disposal ??= enqueue(async () => {
                await hide();
                for (const [name, listener] of Object.entries(listeners)) element.removeEventListener(name, listener);
                modal.dispose();
                if (phase !== 'hidden') {
                    // An unmount cannot retain a dialog even if a host cancels hide.
                    // dispose removes only this instance's backdrop. Restore Bootstrap's
                    // scroll compensation only when no other visible modal needs it.
                    element.classList.remove('show');
                    if (!document.querySelector('.modal.show')) {
                        document.body.classList.toggle('modal-open', bodyWasOpen);
                        for (const { element: target, property, value, priority, attribute } of scrollStyles) {
                            target.style.setProperty(property, value, priority);
                            if (attribute === null) target.removeAttribute(`data-bs-${property}`);
                            else target.setAttribute(`data-bs-${property}`, attribute);
                        }
                    }
                    if (opener?.isConnected) opener.focus({ preventScroll: true });
                }
                // Bootstrap can reattach a detached dialog while its opening fade ends.
                element.remove();
            });
            return disposal;
        }
    };
}
